import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { gql, useLazyQuery } from "@apollo/client";
import { Card, Collapse, Fade, Grid, Icon, Slide, Tooltip } from "@mui/material";
import logo from "assets/images/comedor.png";
import menuIcon from "assets/images/icons/menu.png";
import noMenuIcon from "assets/images/icons/no-menu.png";
import sinTacc from "assets/images/icons/sin-tacc.png";
import vegetariano from "assets/images/icons/vegetariano.png";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";

const normalizeTurno = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const normalizeTurnoKey = (value = "") => {
  const normalized = normalizeTurno(value);
  if (!normalized) return "";
  if (normalized.includes("mediodia")) return "mediodia";
  if (normalized.includes("noche")) return "noche";
  return normalized;
};

function MenuList({ fechaPedido, token, carrito, setCarrito, turnoPedido, usuario, destino }) {
  const [categorias, setCategorias] = useState([]);
  const [currentProducto, setCurrentProducto] = useState(null);
  const [carritoVisible, setCarritoVisible] = useState(true);
  const carritoRef = useRef(null);
  const addingToCartRef = useRef(false);
  const [successTimeout, setSuccessTimeout] = useState(null);
  const [iconKey, setIconKey] = useState(0);
  const [disponibilidadOperativaMap, setDisponibilidadOperativaMap] = useState({});
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const { handleSnackbar } = useContext(MessageManager);
  const idSedeActual = Number(localStorage.getItem("idSede") || 1);
  const idSucursalStock = idSedeActual;
  const isSedeSinStock = idSedeActual === 2;

  const visibleCarrito = carrito.filter((item) => !item.eliminado);
  const total = visibleCarrito.reduce((acc, item) => acc + item.precioCarta * item.cantidad, 0);

  const [getProductos, { loading }] = useLazyQuery(
    gql`
      query getMenusDelDia($filter: JSON, $token: String, $idSede: ID, $destino: String) {
        menusDelDia(filter: $filter, token: $token, idSede: $idSede, destino: $destino) {
          id
          turno
          precio
          fechaMenu
          limiteReservas
          limiteReservasSedeSP
          limiteReservasSedeTR
          limiteReservasSedeC
          cantidadReservas
          cantidadReservasPorSede(idSede: $idSede, destino: $destino)
          producto {
            id
            nombre
            disponibilidad
            esElaborado
            tipoProducto {
              id
              tipo
            }
            categoriaProducto {
              id
              categoria
            }
            files {
              file
            }
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [validarDisponibilidadProducto] = useLazyQuery(
    gql`
      query validarDisponibilidadProducto(
        $idProducto: ID!
        $cantidad: Int!
        $esElaborado: Boolean!
        $idSucursal: ID
        $token: String
      ) {
        validarDisponibilidadProducto(
          idProducto: $idProducto
          cantidad: $cantidad
          esElaborado: $esElaborado
          idSucursal: $idSucursal
          token: $token
        ) {
          ok
          mensaje
        }
      }
    `,
    { fetchPolicy: "no-cache" }
  );

  const [validarDisponibilidadProductos] = useLazyQuery(
    gql`
      query validarDisponibilidadProductos(
        $productos: [DisponibilidadProductoBatchInput!]!
        $idSucursal: ID
        $token: String
      ) {
        validarDisponibilidadProductos(
          productos: $productos
          idSucursal: $idSucursal
          token: $token
        ) {
          idProducto
          ok
        }
      }
    `,
    { fetchPolicy: "no-cache" }
  );

  useEffect(() => {
    const turnoSeleccionadoNormalizado = normalizeTurnoKey(turnoPedido);
    const fechaSeleccionadaISO = dayjs(fechaPedido).format("YYYY-MM-DD");
    if (!turnoSeleccionadoNormalizado) {
      setCategorias([]);
      return;
    }

    getProductos({
      variables: {
        token,
        idSede: idSucursalStock,
        filter: {
          turno: turnoPedido,
          fechaPedido: fechaSeleccionadaISO,
        },
        ...(destino ? { destino } : {})
      },
    }).then((res) => {
      const menusDelDiaFiltrados = (res?.data?.menusDelDia || []).filter((menu) => {
        const menuFecha = dayjs(menu?.fechaMenu);
        const coincideFecha =
          !menu?.fechaMenu ||
          (menuFecha.isValid() && menuFecha.format("YYYY-MM-DD") === fechaSeleccionadaISO);
        return normalizeTurnoKey(menu?.turno) === turnoSeleccionadoNormalizado && coincideFecha;
      });

      let productos =
        menusDelDiaFiltrados
          ?.map((menu) => {
            return {
              ...menu.producto,
              precioCarta: menu.precio,
              fechaMenu: menu.fechaMenu,
              limiteReservas: localStorage.getItem("idSede") == "1" ? menu.limiteReservas : destino == "Serranías Puntanas" ? menu.limiteReservasSedeSP : destino == "Tercera Rotonda" ? menu.limiteReservasSedeTR : destino == "Centenario" ? menu.limiteReservasSedeC : menu.limiteReservas,
              cantidadReservas: localStorage.getItem("idSede") == "1" ? menu.cantidadReservas : menu.cantidadReservasPorSede
            };
          })
          .flat() || [];

      let newCategorias = [];
      productos.forEach((producto) => {
        if (!producto.categoriaProducto) return;
        const categoria = producto.categoriaProducto.categoria;
        const index = newCategorias.findIndex((cat) => cat.categoria === categoria);
        if (index === -1) {
          newCategorias.push({
            categoria,
            productos: [producto],
          });
        } else {
          newCategorias[index].productos.push(producto);
        }
      });
      setCategorias(newCategorias);
    });
  }, [fechaPedido, turnoPedido, destino, token, idSucursalStock]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setCarritoVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (carritoRef.current) {
      observer.observe(carritoRef.current);
    }

    return () => {
      if (carritoRef.current) {
        observer.unobserve(carritoRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isSedeSinStock) {
      setDisponibilidadOperativaMap({});
      return;
    }

    const productosUnicos = new Map();
    (categorias || []).forEach((categoria) => {
      (categoria?.productos || []).forEach((producto) => {
        if (!producto?.id || productosUnicos.has(Number(producto.id))) return;
        productosUnicos.set(Number(producto.id), producto);
      });
    });

    if (productosUnicos.size === 0) {
      setDisponibilidadOperativaMap({});
      return;
    }

    const cantidadesCarrito = (visibleCarrito || []).reduce((acc, item) => {
      const idProd = Number(item?.id);
      if (!idProd) return acc;
      acc[idProd] = (acc[idProd] || 0) + Number(item?.cantidad || 0);
      return acc;
    }, {});

    const productosBatch = [...productosUnicos.values()].map((producto) => ({
      idProducto: Number(producto.id),
      cantidad: Number(cantidadesCarrito[Number(producto.id)] || 0) + 1,
      esElaborado: !!producto.esElaborado,
    }));

    let activo = true;
    validarDisponibilidadProductos({
      variables: {
        productos: productosBatch,
        idSucursal: idSucursalStock,
        token,
      },
    })
      .then(({ data }) => {
        if (!activo) return;
        const mapa = {};
        for (const item of data?.validarDisponibilidadProductos || []) {
          mapa[Number(item.idProducto)] = !!item.ok;
        }
        setDisponibilidadOperativaMap(mapa);
      })
      .catch(() => {
        if (!activo) return;
        setDisponibilidadOperativaMap({});
      });

    return () => {
      activo = false;
    };
  }, [
    categorias,
    carrito,
    idSucursalStock,
    isSedeSinStock,
    token,
    validarDisponibilidadProductos,
  ]);

  return (
    <>
      <Fade in={loading} unmountOnExit>
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          minHeight="50vh"
        >
          <SoftBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <SoftBox
              component="img"
              src={logo}
              alt="UPrO Logo"
              width="10rem"
              p={3}
              sx={{
                animation: "blinking 1.5s infinite",
                "@keyframes blinking": {
                  "0%": {
                    opacity: 1,
                  },
                  "50%": {
                    opacity: 0.5,
                  },
                  "100%": {
                    opacity: 1,
                  },
                },
              }}
            />
            <SoftTypography variant="h6" align="center">
              Estamos buscando los productos...
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </Fade>

      <Fade in={!loading && categorias.length === 0} unmountOnExit>
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          minHeight="50vh"
        >
          <SoftBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <SoftBox
              component="img"
              src={noMenuIcon}
              alt="UPrO Logo"
              width="10rem"
              p={3}
              sx={{
                animation: "shakes 1s",
                "@keyframes shakes": {
                  "0%": {
                    transform: "rotate(0deg)",
                  },
                  "25%": {
                    transform: "rotate(10deg)",
                  },
                  "50%": {
                    transform: "rotate(-20deg)",
                  },
                  "75%": {
                    transform: "rotate(15deg)",
                  },
                  "100%": {
                    transform: "rotate(-5deg)",
                  },
                },
              }}
            />
            <SoftTypography variant="h6" align="center">
              No hay productos disponibles para el turno seleccionado, vuelve más tarde.
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </Fade>
      <Fade in={!loading && categorias.length > 0} unmountOnExit>
        <SoftBox display="flex" flexDirection="column" gap={2}>
          {categorias.map((categoria) => (
            <Card key={categoria.categoria} sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}>
              <SoftBox display="flex" flexDirection="column" gap={2} p={2}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    cursor: "pointer",
                    zIndex: 1,
                  }}
                >
                  <SoftBox display="flex" flexDirection="column">
                    <SoftTypography variant="h6">{categoria.categoria}</SoftTypography>
                    <SoftTypography variant="caption">
                      {categoria.productos.length} productos
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
                <Collapse in={categoria} unmountOnExit mountOnEnter>
                  <Grid container spacing={3} pt={1}>
                    {categoria.productos.map((producto) => {
                      const sinStockOperativo =
                        !isSedeSinStock &&
                        disponibilidadOperativaMap[Number(producto.id)] === false;
                      const agotadoPorCupo =
                        Number(producto.cantidadReservas || 0) >=
                        Number(producto.limiteReservas || 0);

                      return (
                      <Grid item xs={12} sm={6} md={4} key={producto.id}>
                        <Card
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            border: "1px solid rgba(0, 0, 0, 0.12)",
                            transition: "transform 0.3s ease-in-out",
                            "&:hover": {
                              transform: "scale(1.02)",
                            },
                          }}
                        >
                          {producto.files && producto.files.length > 0 ? (
                            <SoftBox
                              sx={{
                                position: "relative",
                                width: "100%",
                                paddingBottom: "100%",
                                overflow: "hidden",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              <SoftBox
                                component="img"
                                src={resolveStoredFileUrl(producto.files[0].file)}
                                alt={producto.nombre}
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  objectPosition: "center",
                                  transition: "transform 0.3s ease-in-out",
                                  cursor: "pointer",
                                  "&:hover": {
                                    transform: "scale(1.05)",
                                  },
                                }}
                              />
                            </SoftBox>
                          ) : (
                            <SoftBox
                              sx={{
                                position: "relative",
                                width: "100%",
                                paddingBottom: "100%",
                                overflow: "hidden",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              <SoftBox
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <SoftBox
                                  component="img"
                                  src={
                                    producto?.tipoProducto?.tipo === "Vegetariano"
                                      ? vegetariano
                                      : producto?.tipoProducto?.tipo === "Libre de gluten"
                                        ? sinTacc
                                        : menuIcon
                                  }
                                  alt={producto.nombre}
                                  sx={{
                                    width: "50%",
                                    height: "50%",
                                    objectFit: "contain",
                                    transition: "transform 0.3s ease-in-out",
                                    cursor: "pointer",
                                    "&:hover": {
                                      transform: "scale(1.05)",
                                    },
                                  }}
                                />
                              </SoftBox>
                            </SoftBox>
                          )}
                          <SoftBox
                            display="flex"
                            gap={2}
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                          >
                            <SoftBox display="flex" flexDirection="column">
                              <SoftTypography variant="h6">{producto.nombre}</SoftTypography>
                              {producto?.tipoProducto?.tipo && (
                                <SoftTypography variant="h7" fontWeight="regular">
                                  {producto.tipoProducto.tipo}
                                </SoftTypography>
                              )}
                            </SoftBox>
                            <SoftBox>
                              <SoftBadge
                                color="primary"
                                badgeContent={`$${formatMoneyPunto(producto.precioCarta)}`}
                                container
                              />
                            </SoftBox>
                          </SoftBox>
                          <SoftBox
                            p={2}
                            pt={0}
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={2}
                          >
                            <SoftButton
                              color="uproGreen"
                              circular
                              fullWidth
                              disabled={
                                (carrito?.filter((item) => !item.eliminado).length > 0 &&
                                  !usuario?.vip) ||
                                agotadoPorCupo ||
                                sinStockOperativo ||
                                producto?.precioCarta <= 0 ||
                                isAddingToCart
                              }
                              onClick={async () => {
                                if (addingToCartRef.current) return;
                                addingToCartRef.current = true;
                                setIsAddingToCart(true);
                                const cantidadEnCarrito = (visibleCarrito || [])
                                  .filter((item) => Number(item?.id) === Number(producto.id))
                                  .reduce(
                                    (acc, item) => acc + Number(item?.cantidad || 0),
                                    0
                                  );
                                const cantidadAAgregar =
                                  currentProducto?.id === producto.id
                                    ? Number(currentProducto?.cantidad || 1)
                                    : 1;
                                try {
                                  if (!isSedeSinStock) {
                                    try {
                                      const { data } = await validarDisponibilidadProducto({
                                        variables: {
                                          idProducto: producto.id,
                                          cantidad: cantidadEnCarrito + cantidadAAgregar,
                                          esElaborado: !!producto.esElaborado,
                                          idSucursal: idSucursalStock,
                                          token,
                                        },
                                      });

                                      if (!data?.validarDisponibilidadProducto?.ok) {
                                        setDisponibilidadOperativaMap((prev) => ({
                                          ...prev,
                                          [Number(producto.id)]: false,
                                        }));
                                        handleSnackbar(
                                          data?.validarDisponibilidadProducto?.mensaje ||
                                            "Stock agotado para este producto.",
                                          "error"
                                        );
                                        return;
                                      }
                                    } catch (error) {
                                      handleSnackbar(
                                        "No se pudo validar stock en este momento.",
                                        "error"
                                      );
                                      return;
                                    }
                                  }

                                  setCarrito((prev) => {
                                    const index = prev.findIndex(
                                      (item) => item.id === producto.id && !item.eliminado
                                    );
                                    const cantidadSolicitada = Number(currentProducto?.cantidad || 1);

                                    if (index === -1) {
                                      return [
                                        ...prev,
                                        {
                                          ...producto,
                                          cantidad: !usuario?.vip ? 1 : cantidadSolicitada,
                                          uniqueId: `${producto.id}-${Date.now()}`,
                                        },
                                      ];
                                    }

                                    if (!usuario?.vip) {
                                      return prev;
                                    }

                                    const newCarrito = [...prev];
                                    newCarrito[index].cantidad += cantidadSolicitada;
                                    return newCarrito;
                                  });
                                  setCurrentProducto(null);
                                  setIconKey((prev) => prev + 1);
                                  const newTimeout = setTimeout(() => {
                                    setSuccessTimeout(null);
                                    setIconKey((prev) => prev + 1);
                                  }, 3000);
                                  if (successTimeout) {
                                    setIconKey((prev) => prev + 1);
                                    clearTimeout(successTimeout);
                                  }
                                  setSuccessTimeout(newTimeout);
                                } finally {
                                  addingToCartRef.current = false;
                                  setIsAddingToCart(false);
                                }
                              }}
                            >
                              {sinStockOperativo
                                ? "Sin stock"
                                : agotadoPorCupo
                                  ? "Agotado"
                                  : "Agregar al carrito"}
                              &nbsp;
                              {!sinStockOperativo && !agotadoPorCupo && (
                                <Icon>add_shopping_cart</Icon>
                              )}
                            </SoftButton>
                          </SoftBox>
                        </Card>
                      </Grid>
                      );
                    })}
                  </Grid>
                </Collapse>
              </SoftBox>
            </Card>
          ))}
          <SoftBox ref={carritoRef}>
            <Card
              sx={{
                border: "1px solid rgba(0, 0, 0, 0.12)",
                zIndex: 2,
              }}
            >
              <SoftBox
                display="flex"
                justifyContent="space-between"
                px={2}
                py={1}
                sx={{
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                }}
              >
                <SoftBox display="flex" alignItems="center">
                  <SoftBox mr={1} display="flex" alignItems="center">
                    <Icon color="primary">shopping_cart</Icon>
                  </SoftBox>
                  <SoftTypography variant="h6" fontWeight="bold">
                    Carrito
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
              {carrito.filter((item) => !item.eliminado).length === 0 && (
                <SinDatos
                  icono="production_quantity_limits"
                  titulo="No hay productos"
                  mensaje="Agrega productos para continuar"
                />
              )}
              <Collapse
                in={visibleCarrito.length > 0}
                sx={{
                  maxHeight: {
                    xs: "100%",
                    sm: "50vh",
                  },
                  overflowY: "auto",
                }}
              >
                {carrito.map((producto, index) => (
                  <Slide
                    in={!producto.eliminado}
                    key={producto.uniqueId || index}
                    direction="right"
                    mountOnEnter
                    unmountOnExit
                  >
                    <SoftBox>
                      <SoftBox
                        px={2}
                        py={1}
                        sx={{
                          borderBottom:
                            index === carrito.length - 1 ||
                              producto.uniqueId ===
                              carrito.filter((item) => !item.eliminado).slice(-1)[0]?.uniqueId
                              ? "none"
                              : "1px solid rgba(0, 0, 0, 0.12)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <SoftBox
                          display={{
                            xs: "flex-row",
                            sm: "flex",
                          }}
                          justifyContent="space-between"
                        >
                          <SoftBox
                            display="flex"
                            alignItems="center"
                            justifyContent={{
                              xs: "space-between",
                              sm: "flex-start",
                            }}
                          >
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox
                                mr={{
                                  xs: 0,
                                  sm: 1,
                                }}
                                sx={{
                                  display: {
                                    xs: "none",
                                    sm: "block",
                                  },
                                }}
                              >
                                <Tooltip
                                  title={producto.categoriaProducto.categoria}
                                  placement="top"
                                >
                                  <Icon
                                    sx={{
                                      color: getUproColor("uproYellow"),
                                      cursor: "pointer",
                                    }}
                                  >
                                    restaurant_menu
                                  </Icon>
                                </Tooltip>
                              </SoftBox>
                              <SoftBox display="flex" gap={1} alignItems="center">
                                <SoftTypography variant="h6">
                                  {makeNiceText(producto.nombre)}
                                </SoftTypography>
                                <SoftBadge
                                  color="uproYellow"
                                  badgeContent={`x${producto.cantidad}`}
                                />
                              </SoftBox>
                            </SoftBox>
                          </SoftBox>
                          <SoftBox
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={2}
                            mt={{
                              xs: 2,
                              sm: 0,
                            }}
                          >
                            <SoftBox display="flex" alignItems="center" gap={2}>
                              <SoftBox display="flex" flexDirection="column" alignItems="flex-end">
                                <SoftTypography
                                  variant="h6"
                                  color={"text"}
                                  sx={{
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  $ {formatMoneyPunto(producto.precioCarta * producto.cantidad)}
                                </SoftTypography>
                                <SoftTypography
                                  variant="caption"
                                  color={"text"}
                                  sx={{
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  $ {formatMoneyPunto(producto.precioCarta)} c/u
                                </SoftTypography>
                              </SoftBox>
                              <SoftBox>
                                <Tooltip title={"Quitar del carrito"} placement="top">
                                  <SoftButton
                                    color="primary"
                                    circular
                                    iconOnly
                                    fullWidth={{
                                      xs: true,
                                      sm: false,
                                    }}
                                    onClick={() => {
                                      const newCarrito = JSON.parse(JSON.stringify(carrito));
                                      newCarrito[index].eliminado = true;
                                      setCarrito(newCarrito);
                                    }}
                                  >
                                    <Icon>delete</Icon>
                                  </SoftButton>
                                </Tooltip>
                              </SoftBox>
                            </SoftBox>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Slide>
                ))}
              </Collapse>
            </Card>
            <Card
              sx={{
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                position: "sticky",
                marginTop: -2,
                zIndex: 1,
              }}
            >
              <SoftBox bgColor="primary">
                <SoftBox display="flex" justifyContent="center" bgColor="primary" p={2} pt={4}>
                  <SoftTypography variant="h6" fontWeight="bold" color="white">
                    Total: $ {formatMoneyPunto(total)}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Card>
          </SoftBox>
          {!carritoVisible && carrito?.filter((item) => !item.eliminado).length > 0 && (
            <Slide direction="up" in={!carritoVisible} mountOnEnter unmountOnExit>
              <SoftBox
                px={2}
                mb={2}
                sx={{
                  position: "sticky",
                  zIndex: 9999,
                  bottom: 0,
                  right: 0,
                  width: {
                    xs: "100%",
                    sm: "30%",
                  },
                  alignSelf: "flex-end",
                }}
              >
                <SoftButton
                  fullWidth
                  color={successTimeout ? "uproGreen" : "primary"}
                  circular
                  onClick={() => carritoRef.current?.scrollIntoView({ behavior: "smooth" })}
                  sx={{
                    transition: "background-color 0.3s ease",
                    animation: "float 3s ease-in-out infinite",
                    "@keyframes float": {
                      "0%": {
                        transform: "translateY(0)",
                      },
                      "50%": {
                        transform: "translateY(-10px)",
                      },
                      "100%": {
                        transform: "translateY(0)",
                      },
                    },
                  }}
                >
                  {successTimeout ? "Producto agregado al carrito" : "Ver carrito"}&nbsp;
                  <Icon
                    key={iconKey}
                    sx={{
                      animation: "shakes 1s",
                      "@keyframes shakes": {
                        "0%": {
                          transform: "rotate(0deg)",
                        },
                        "25%": {
                          transform: "rotate(10deg)",
                        },
                        "50%": {
                          transform: "rotate(-20deg)",
                        },
                        "75%": {
                          transform: "rotate(15deg)",
                        },
                        "100%": {
                          transform: "rotate(-5deg)",
                        },
                      },
                    }}
                  >
                    {successTimeout ? "done" : "shopping_cart"}
                  </Icon>
                </SoftButton>
              </SoftBox>
            </Slide>
          )}
        </SoftBox>
      </Fade>
    </>
  );
}

MenuList.propTypes = {
  fechaPedido: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  carrito: PropTypes.array.isRequired,
  setCarrito: PropTypes.func.isRequired,
  turnoPedido: PropTypes.string.isRequired,
  usuario: PropTypes.object,
  destino: PropTypes.string,
};

export default MenuList;
